import type { InvestmentLayer, ProgramLayerId } from '@/types/roadmap.types';

const FALLBACK_LABELS: Record<string, string> = {
  rd: 'Core R&D',
  testing: 'Testing & Validation',
  prototyping: 'Prototyping',
  manufacturing: 'Manufacturing',
  general: 'General',
  commercial: 'Commercial Growth',
  deployment: 'Deployment',
  scaling: 'Scaling',
};

const FALLBACK_SHORT_LABELS: Record<string, string> = {
  rd: 'R&D',
  testing: 'Testing',
  prototyping: 'Prototyping',
  manufacturing: 'Mfg',
  general: 'General',
  commercial: 'Commercial',
  deployment: 'Deploy',
  scaling: 'Scale',
};

const LAYER_COLORS: Record<string, string> = {
  rd: 'bg-violet-50 text-violet-700 border-violet-200 print:bg-violet-50',
  testing: 'bg-sky-50 text-sky-700 border-sky-200 print:bg-sky-50',
  prototyping: 'bg-amber-50 text-amber-700 border-amber-200 print:bg-amber-50',
  manufacturing: 'bg-emerald-50 text-emerald-700 border-emerald-200 print:bg-emerald-50',
  general: 'bg-gray-50 text-gray-600 border-gray-200 print:bg-gray-50',
  commercial: 'bg-rose-50 text-rose-700 border-rose-200 print:bg-rose-50',
  deployment: 'bg-teal-50 text-teal-700 border-teal-200 print:bg-teal-50',
  scaling: 'bg-orange-50 text-orange-700 border-orange-200 print:bg-orange-50',
};

const LAYER_ACCENT: Record<string, string> = {
  rd: 'border-t-blue-500',
  testing: 'border-t-amber-500',
  prototyping: 'border-t-violet-500',
  manufacturing: 'border-t-emerald-500',
  general: 'border-t-gray-400',
  commercial: 'border-t-rose-500',
  deployment: 'border-t-teal-500',
  scaling: 'border-t-orange-500',
};

const DEFAULT_FALLBACK_COLOR = LAYER_COLORS.general;
const DEFAULT_FALLBACK_ACCENT = 'border-t-primary';

function buildLookup(layers: InvestmentLayer[] | undefined): Map<string, InvestmentLayer> | null {
  if (!layers || layers.length === 0) return null;
  return new Map(layers.map((l) => [l.id, l]));
}

export function getLayerLabel(id: ProgramLayerId, configLayers?: InvestmentLayer[]): string {
  const lookup = buildLookup(configLayers);
  return lookup?.get(id)?.label ?? FALLBACK_LABELS[id] ?? id;
}

export function getLayerShortLabel(id: ProgramLayerId, configLayers?: InvestmentLayer[]): string {
  const lookup = buildLookup(configLayers);
  const configLabel = lookup?.get(id)?.label;
  if (configLabel && configLabel.length <= 12) return configLabel;
  return FALLBACK_SHORT_LABELS[id] ?? FALLBACK_LABELS[id] ?? id;
}

export function getLayerColorClass(id: ProgramLayerId): string {
  return LAYER_COLORS[id] ?? DEFAULT_FALLBACK_COLOR;
}

export function getLayerAccentClass(id: ProgramLayerId): string {
  return LAYER_ACCENT[id] ?? DEFAULT_FALLBACK_ACCENT;
}

export function getLayerDescription(id: ProgramLayerId, configLayers?: InvestmentLayer[]): string {
  const lookup = buildLookup(configLayers);
  return lookup?.get(id)?.description ?? '';
}

export function getLayerOrder(configLayers?: InvestmentLayer[]): ProgramLayerId[] {
  if (configLayers && configLayers.length > 0) {
    return configLayers.map((l) => l.id);
  }
  return ['rd', 'testing', 'prototyping', 'manufacturing', 'general'];
}
